// src/pages/LandingPage.jsx
// PAM Portal Landing Page — Tenant selection

import React from 'react';
import { useNavigate } from 'react-router-dom';
import ContactFooter from '../components/ContactFooter';

// ── T321 feature gate ──
// T321 (unlaunched) is gated behind a build-time env var so the SAME shared source
// ships to both stages but T321 is only reachable where the flag is on:
//   beta/pre-prod build  -> REACT_APP_ENABLE_T321=true  (tile Active/WIP, /t321 usable)
//   prod build (default) -> unset/false                 (tile "Coming Soon", dormant)
// This lets T178 P0 fixes deploy to prod without exposing unlaunched T321.
// To LAUNCH T321 in prod later: set REACT_APP_ENABLE_T321=true in the prod build
// (and remove status:'wip' below for the green "Active" badge).
const T321_ENABLED = String(process.env.REACT_APP_ENABLE_T321).toLowerCase() === 'true';

const tenants = [
    { id: 'T178', name: 'Tenant 178', num: '178', desc: 'Paragon Investigations', active: true, color: '#22c55e', route: '/request' },
    { id: 'T290', name: 'Tenant 290', num: '290', desc: 'BRI', active: false, color: '#94a3b8' },
    { id: 'T310', name: 'Tenant 310', num: '310', desc: 'Base PI', active: false, color: '#94a3b8' },
    // T321 backend/UI code is complete and preserved (route '/t321' + T321Request.jsx remain wired in App.js).
    // Gated by REACT_APP_ENABLE_T321 (see T321_ENABLED above). When the flag is off it renders
    // as a non-clickable "Coming Soon" tile; when on, it's an Active/WIP clickable tile.
    { id: 'T321', name: 'Tenant 321', num: '321', desc: 'Paragon', active: T321_ENABLED, color: '#f59e0b', route: '/t321', status: T321_ENABLED ? 'wip' : undefined },
    { id: 'T333', name: 'Tenant 333', num: '333', desc: 'Paragon', active: false, color: '#94a3b8' },
    { id: 'T398', name: 'Medux + 398', num: '398', desc: 'Medux', active: false, color: '#94a3b8' },
];

const LandingPage = ({ userAlias }) => {
    const navigate = useNavigate();

    return (
        <div style={{ minHeight: '100vh', background: '#fff' }}>
            {/* Minimal top bar */}
            <div style={{ 
                padding: '14px 40px', 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                borderBottom: '1px solid #e9ecef',
                background: '#fafafa'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <span style={{ fontSize: '20px' }}>⚡</span>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: '#333' }}>Permissions & Access Management</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                    <a href="/my-requests" style={{ fontSize: '14px', color: '#0073bb', textDecoration: 'none', fontWeight: '500' }}>📊 My Requests</a>
                    {userAlias && (
                        <span style={{ fontSize: '13px', color: '#555', padding: '4px 12px', borderRadius: '16px', background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                            ✨ Welcome, <strong>{userAlias}</strong>
                        </span>
                    )}
                </div>
            </div>

            {/* Hero section */}
            <div style={{ 
                padding: '60px 40px 40px', 
                textAlign: 'center',
                background: 'linear-gradient(180deg, #1a1a2e 0%, #232946 100%)',
                color: '#fff'
            }}>
                <h1 style={{ fontSize: '32px', fontWeight: '800', margin: '0 0 12px', letterSpacing: '-0.5px' }}>
                    Permissions & Access Management Portal
                </h1>
                <p style={{ fontSize: '16px', color: '#b8c1ec', margin: '0 0 8px', maxWidth: '600px', marginLeft: 'auto', marginRight: 'auto' }}>
                    Centralized access provisioning for SPS Paragon Tenants
                </p>
            </div>

            {/* Tenant selection */}
            <div style={{ maxWidth: '1000px', margin: '0 auto', padding: '48px 40px' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '700', color: '#333', marginBottom: '8px', textAlign: 'center' }}>
                    Select Your Tenant
                </h2>
                <p style={{ fontSize: '14px', color: '#666', textAlign: 'center', marginBottom: '32px' }}>
                    Choose a Paragon Tenant to request access for
                </p>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                    {tenants.map(tenant => (
                        <div 
                            key={tenant.id}
                            onClick={() => { 
                                if (tenant.active) {
                                    // Clear any saved form state — user is starting fresh
                                    localStorage.removeItem('pam_form_state');
                                    navigate(tenant.route || '/request'); 
                                }
                            }}
                            style={{
                                padding: '24px',
                                borderRadius: '12px',
                                border: `2px solid ${tenant.status === 'wip' ? '#fde68a' : tenant.active ? '#22c55e' : '#e2e6ea'}`,
                                background: tenant.status === 'wip' ? '#fffef5' : tenant.active ? '#f0fdf4' : '#f9fafb',
                                cursor: tenant.active ? 'pointer' : 'default',
                                transition: 'all 0.2s ease',
                                opacity: tenant.active ? 1 : 0.7,
                                position: 'relative',
                            }}
                            onMouseOver={(e) => { if (tenant.active) { e.currentTarget.style.borderColor = '#16a34a'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(34,197,94,0.15)'; }}}
                            onMouseOut={(e) => { if (tenant.active) { e.currentTarget.style.borderColor = '#22c55e'; e.currentTarget.style.boxShadow = 'none'; }}}
                        >
                            {/* Status badge */}
                            <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
                                {tenant.active && tenant.status !== 'wip' ? (
                                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#dcfce7', color: '#166534', border: '1px solid #bbf7d0' }}>Active</span>
                                ) : tenant.status === 'wip' ? (
                                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#fef9c3', color: '#854d0e', border: '1px solid #fde68a' }}>Work in Progress</span>
                                ) : (
                                    <span style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700', background: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}>Coming Soon</span>
                                )}
                            </div>

                            <div style={{ 
                                width: '48px', height: '48px', borderRadius: '12px', 
                                background: tenant.active ? tenant.color : '#e2e8f0',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                marginBottom: '12px'
                            }}>
                                <span style={{ fontSize: '16px', fontWeight: '800', color: tenant.active ? '#fff' : '#64748b' }}>{tenant.num}</span>
                            </div>
                            <div style={{ fontWeight: '700', fontSize: '16px', color: tenant.active ? '#166534' : '#64748b', marginBottom: '4px' }}>
                                {tenant.name}
                            </div>
                            <div style={{ fontSize: '13px', color: '#666' }}>{tenant.desc}</div>
                            
                            {tenant.active && (
                                <div style={{ marginTop: '16px', fontSize: '13px', fontWeight: '600', color: '#22c55e' }}>
                                    Request Access →
                                </div>
                            )}
                            {!tenant.active && (
                                <div style={{ marginTop: '16px', fontSize: '12px', color: '#94a3b8' }}>
                                    Will be launched soon
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            <ContactFooter />
        </div>
    );
};

export default LandingPage;
